import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { usePageMeta } from "@/lib/seo";

const SERVICES = [
  {
    n: "01",
    title: "Management Consultancy",
    body: "Practical support with the way your business is organised and managed. We work with you to review priorities, consider improvements and develop an approach suited to your commercial goals.",
  },
  {
    n: "02",
    title: "Business Advisory",
    body: "An outside perspective on the decisions facing your business. We help you assess options, work through challenges and identify practical next steps, with support tailored to your circumstances.",
  },
  {
    n: "03",
    title: "International Business Development",
    body: "Support for businesses exploring opportunities in different markets and jurisdictions. We help you consider the commercial and operational requirements, understand what needs to be explored locally and coordinate with relevant professional advisers where needed.",
  },
  {
    n: "04",
    title: "Business Support and Coordination",
    body: "Practical assistance with moving plans forward, organising information and coordinating with external advisers and service providers. This may include banking and payments support, with services and approval decisions remaining the responsibility of the relevant providers.",
  },
];

export default function Services() {
  usePageMeta(
    "Services — W Group Consulting",
    "Management consultancy, business advisory, international business development and business support — practical help shaped around each business.",
    "/services"
  );

  return (
    <>
      <PageHero
        eyebrow="Services"
        titleLines={["Business consultancy", "and advisory"]}
        description="We help business owners and management teams explore their options, plan their next steps and move their businesses forward. Our support is shaped around each business, whether the priority is improving existing operations, pursuing growth or considering a new jurisdiction."
      />
      <section className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32" data-testid="services-list">
        <div className="flex flex-col gap-0 border-t border-ink/10">
          {SERVICES.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.05}>
              <article className="grid gap-8 border-b border-ink/10 py-14 lg:grid-cols-[80px_1fr_1.4fr] lg:gap-14" data-testid={`service-${s.n}`}>
                <span className="font-display text-xl italic text-gold">{s.n}</span>
                <h2 className="font-display text-3xl text-ink lg:text-4xl">{s.title}</h2>
                <p className="max-w-xl self-center text-base leading-relaxed text-gray-600">{s.body}</p>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-white py-24 lg:py-32" data-testid="services-cta">
        <div className="mx-auto max-w-7xl px-6 text-center lg:px-10">
          <Reveal>
            <h2 className="mx-auto max-w-3xl font-display text-4xl leading-tight text-ink sm:text-5xl">
              Let&rsquo;s discuss your <span className="italic text-gold">next step</span>.
            </h2>
            <p className="mx-auto mt-8 max-w-xl text-base leading-relaxed text-gray-600">
              Whether you are reviewing your current business or exploring a
              new opportunity, we can help you consider the way forward.
            </p>
            <p className="mx-auto mt-6 max-w-xl text-sm leading-relaxed text-gray-500">
              Where specialist advice is required, we coordinate with
              appropriately qualified professional advisers.
            </p>
            <Link
              to="/contact"
              data-testid="services-cta-contact"
              className="group mt-12 inline-flex items-center gap-3 bg-ink px-8 py-4 text-sm font-medium tracking-wide text-paper transition-colors duration-300 hover:bg-gold hover:text-ink"
            >
              Start a conversation
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>
    </>
  );
}
